import type { ClientMessage, ServerMessage } from '@shared/protocol';
import type { GamepadSource } from '../input/gamepad';
import { computeSector } from '../input/emote-wheel';
import type { GsapRegistry } from '../renderer/vfx/gsap-registry';
import {
  createEmoteBubbleRenderer,
  type EmoteTextureGetter,
  type RigGetter,
} from '../renderer/emote-bubble-renderer';
import { createEmoteWheel } from '../ui/components/emote-wheel';
import { EMOTE_SFX_MAP } from '../audio';
import type { SfxId } from '../audio';
import type { EmoteId } from './types';

type KeyboardSource = 'keyboard-slot-0' | 'keyboard-slot-1';
type InputSource = KeyboardSource | 'gamepad-slot-0' | 'pointer-button';
export type EmoteRuntimeDiagnostic =
  | {
      type: 'emote_send_attempt';
      playerId: string;
      emoteId: EmoteId;
      source: InputSource;
    }
  | {
      type: 'emote_send_blocked_cooldown';
      playerId: string;
      emoteId: EmoteId;
      source: InputSource;
      remainingMs: number;
    };

export interface EmoteRuntimeDeps {
  gsapRegistry: GsapRegistry;
  mount: HTMLElement;
  keyboardTarget?: EventTarget;
  pointerTarget?: Window;
  documentRef?: Document;
  gamepad?: GamepadSource;
  getOwnedPlayerIds: () => readonly string[];
  isEmoteInputEnabled: () => boolean;
  send: (message: ClientMessage) => void;
  subscribeServerMessages: (handler: (message: ServerMessage) => void) => () => void;
  getPlayerRig: RigGetter;
  getEmoteTexture: EmoteTextureGetter;
  playSfxForPlayer: (id: SfxId, playerId: string | null | undefined) => void;
  iconUrls?: Partial<Record<EmoteId, string>>;
  isTextEntryTarget?: (target: EventTarget | null) => boolean;
  now?: () => number;
  onDiagnostic?: (event: EmoteRuntimeDiagnostic) => void;
}

export interface EmoteRuntimeHandle {
  tick(): void;
  isOpen(): boolean;
  clearPlayer(playerId: string): void;
  dispose(): void;
}

const KEYBOARD_SLOT_BY_CODE: Readonly<Record<string, 0 | 1>> = {
  KeyT: 0,
  Numpad0: 1,
};

const GAMEPAD_INDEX = 0;
const GAMEPAD_LT_BUTTON_INDEX = 6;
const SELECTION_DEADZONE_PX = 24;
const GAMEPAD_AIM_DEADZONE = 0.25;
const EMOTE_UI_Z_INDEX = 560;
const EMOTE_COOLDOWN_MS = 2_000;

interface ActiveWheel {
  source: InputSource;
  playerId: string;
  selectedEmoteId: EmoteId | null;
}

function defaultIsTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return (
    target.closest(
      ['input', 'select', 'textarea', '[contenteditable="true"]', '[role="textbox"]'].join(','),
    ) != null
  );
}

function emoteIdFromVector(x: number, y: number, deadzone: number): EmoteId | null {
  if (Math.hypot(x, y) < deadzone) return null;
  return (computeSector(x, y) + 1) as EmoteId;
}

export function createEmoteRuntime(deps: EmoteRuntimeDeps): EmoteRuntimeHandle {
  const keyboardTarget = deps.keyboardTarget ?? window;
  const pointerTarget = deps.pointerTarget ?? window;
  const documentRef = deps.documentRef ?? document;
  const isTextEntryTarget = deps.isTextEntryTarget ?? defaultIsTextEntryTarget;
  const now = deps.now ?? (() => performance.now());
  const wheel = createEmoteWheel({
    gsapRegistry: deps.gsapRegistry,
    mount: deps.mount,
    getAim: () => null,
    iconUrls: deps.iconUrls,
    keyboardShortcuts: false,
  });
  const bubbles = createEmoteBubbleRenderer({
    gsapRegistry: deps.gsapRegistry,
    subscribe: deps.subscribeServerMessages,
    getPlayerRig: deps.getPlayerRig,
    getEmoteTexture: deps.getEmoteTexture,
  });
  let active: ActiveWheel | null = null;
  let disposed = false;
  let gamepadLtPressed = false;
  const lastEmoteAttemptAtByPlayer = new Map<string, number>();

  const unsubscribeSfx = deps.subscribeServerMessages((message) => {
    if (message.type !== 'emote_played') return;
    lastEmoteAttemptAtByPlayer.set(message.playerId, now());
    deps.playSfxForPlayer(EMOTE_SFX_MAP[message.emoteId], message.playerId);
  });

  const emoteButton = document.createElement('button');
  emoteButton.type = 'button';
  emoteButton.className = 'ui-emote-button';
  emoteButton.setAttribute('aria-label', 'Open emotes');
  emoteButton.setAttribute('data-tutorial-target', 'emote-button');
  emoteButton.title = 'Open emotes';
  emoteButton.style.cssText = [
    'position: fixed',
    'left: 50%',
    'bottom: 104px',
    'transform: translateX(-50%)',
    'width: 72px',
    'height: 72px',
    'display: none',
    'align-items: center',
    'justify-content: center',
    'padding: 0',
    'border-radius: 50%',
    'border: 3px solid rgba(255,255,255,0.88)',
    'background: rgba(30,35,50,0.88)',
    'box-shadow: 0 5px 0 rgba(0,0,0,0.38), 0 10px 20px rgba(0,0,0,0.30)',
    `z-index: ${EMOTE_UI_Z_INDEX}`,
    'pointer-events: auto',
    'cursor: pointer',
    'touch-action: manipulation',
  ].join('; ');
  const emoteButtonIconUrl = deps.iconUrls?.[1];
  if (emoteButtonIconUrl != null && emoteButtonIconUrl !== '') {
    const icon = document.createElement('img');
    icon.alt = '';
    icon.draggable = false;
    icon.src = emoteButtonIconUrl;
    icon.style.cssText = [
      'width: 58px',
      'height: 58px',
      'display: block',
      'border-radius: 50%',
      'object-fit: cover',
      'pointer-events: none',
    ].join('; ');
    emoteButton.appendChild(icon);
  } else {
    emoteButton.textContent = ':)';
    emoteButton.style.fontFamily =
      "var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)";
    emoteButton.style.fontSize = '22px';
    emoteButton.style.fontWeight = '900';
    emoteButton.style.color = '#FFFFFF';
    emoteButton.style.textShadow = '0 2px 0 #000000';
  }
  deps.mount.appendChild(emoteButton);
  refreshEmoteButtonVisibility();

  function ownedPlayerIdForSlot(slot: 0 | 1): string | null {
    return deps.getOwnedPlayerIds()[slot] ?? null;
  }

  function firstOwnedPlayerId(): string | null {
    return deps.getOwnedPlayerIds()[0] ?? null;
  }

  function isEmoteInputEnabled(): boolean {
    return deps.isEmoteInputEnabled();
  }

  function ensureEmoteButtonMounted(): void {
    if (!disposed && emoteButton.parentNode !== deps.mount) {
      deps.mount.appendChild(emoteButton);
    }
  }

  function refreshEmoteButtonVisibility(): void {
    ensureEmoteButtonMounted();
    emoteButton.style.display =
      isEmoteInputEnabled() && deps.getOwnedPlayerIds().length > 0 ? 'flex' : 'none';
  }

  function showActive(next: ActiveWheel): void {
    active = next;
    wheel.show(next.selectedEmoteId);
  }

  function cancelActive(): void {
    if (active == null) return;
    active = null;
    wheel.hide();
  }

  function openForKeyboard(slot: 0 | 1): void {
    if (!isEmoteInputEnabled()) return;
    const playerId = ownedPlayerIdForSlot(slot);
    if (playerId == null) return;
    cancelActive();
    showActive({
      source: slot === 0 ? 'keyboard-slot-0' : 'keyboard-slot-1',
      playerId,
      selectedEmoteId: null,
    });
  }

  function openForGamepad(): void {
    if (!isEmoteInputEnabled()) return;
    const ownedPlayerIds = deps.getOwnedPlayerIds();
    if (ownedPlayerIds.length !== 1) return;
    cancelActive();
    showActive({
      source: 'gamepad-slot-0',
      playerId: ownedPlayerIds[0]!,
      selectedEmoteId: null,
    });
  }

  function openForPointerButton(): void {
    if (!isEmoteInputEnabled()) return;
    const playerId = firstOwnedPlayerId();
    if (playerId == null) return;
    cancelActive();
    showActive({
      source: 'pointer-button',
      playerId,
      selectedEmoteId: null,
    });
  }

  function fireActive(): void {
    const current = active;
    if (current?.selectedEmoteId == null) {
      active = null;
      wheel.hide();
      return;
    }

    const currentTime = now();
    const lastAttemptAt = lastEmoteAttemptAtByPlayer.get(current.playerId) ?? -Infinity;
    const remainingMs = EMOTE_COOLDOWN_MS - (currentTime - lastAttemptAt);
    if (remainingMs > 0) {
      deps.onDiagnostic?.({
        type: 'emote_send_blocked_cooldown',
        playerId: current.playerId,
        emoteId: current.selectedEmoteId,
        source: current.source,
        remainingMs: Math.ceil(remainingMs),
      });
      wheel.show(current.selectedEmoteId);
      return;
    }

    active = null;
    wheel.hide();
    lastEmoteAttemptAtByPlayer.set(current.playerId, currentTime);
    deps.onDiagnostic?.({
      type: 'emote_send_attempt',
      playerId: current.playerId,
      emoteId: current.selectedEmoteId,
      source: current.source,
    });
    deps.send({
      type: 'emote',
      playerId: current.playerId,
      emoteId: current.selectedEmoteId,
    });
  }

  function fireEmoteId(emoteId: EmoteId): void {
    if (active == null) return;
    active.selectedEmoteId = emoteId;
    fireActive();
  }

  function setSelected(emoteId: EmoteId | null): void {
    if (active == null || active.selectedEmoteId === emoteId) return;
    active.selectedEmoteId = emoteId;
    wheel.setSelectedEmoteId(emoteId);
  }

  function handleKeydown(event: Event): void {
    if (disposed) return;
    const keyEvent = event as KeyboardEvent;
    if (keyEvent.code === 'Escape' && active != null) {
      event.preventDefault();
      cancelActive();
      return;
    }
    if (keyEvent.defaultPrevented || keyEvent.repeat || isTextEntryTarget(keyEvent.target)) return;
    const slot = KEYBOARD_SLOT_BY_CODE[keyEvent.code];
    if (slot == null) return;
    event.preventDefault();
    openForKeyboard(slot);
  }

  function handleKeyup(event: Event): void {
    if (disposed || active == null) return;
    const keyEvent = event as KeyboardEvent;
    const slot = KEYBOARD_SLOT_BY_CODE[keyEvent.code];
    if (slot == null) return;
    const source: KeyboardSource = slot === 0 ? 'keyboard-slot-0' : 'keyboard-slot-1';
    if (active.source !== source) return;
    event.preventDefault();
    fireActive();
  }

  function handlePointerMove(event: Event): void {
    if (disposed || active == null) return;
    if (active.source !== 'keyboard-slot-0' && active.source !== 'keyboard-slot-1') return;
    const pointerEvent = event as PointerEvent;
    const x = pointerEvent.clientX - Math.max(pointerTarget.innerWidth, 1) / 2;
    const y = Math.max(pointerTarget.innerHeight, 1) / 2 - pointerEvent.clientY;
    setSelected(emoteIdFromVector(x, y, SELECTION_DEADZONE_PX));
  }

  function handlePointerDown(event: Event): void {
    if (disposed || active == null) return;
    const pointerEvent = event as PointerEvent;
    if (isWheelOrButtonTarget(pointerEvent.target)) return;
    if (pointerEvent.button !== 0) return;
    if (active.source === 'pointer-button') {
      cancelActive();
    }
    pointerEvent.preventDefault();
    pointerEvent.stopImmediatePropagation();
  }

  function isWheelOrButtonTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    return target.closest('.ui-emote-wheel__sector, .ui-emote-button') != null;
  }

  function handleBlur(): void {
    cancelActive();
  }

  function handleVisibilityChange(): void {
    if (documentRef.visibilityState === 'hidden') {
      cancelActive();
    }
  }

  function tickGamepad(): void {
    const gamepad = deps.gamepad;
    if (gamepad == null) return;
    gamepad.poll();
    const snapshot = gamepad.getSnapshot(GAMEPAD_INDEX);
    const pressed =
      snapshot?.connected === true && snapshot.buttons[GAMEPAD_LT_BUTTON_INDEX]?.pressed === true;

    if (pressed && !gamepadLtPressed) {
      openForGamepad();
    }
    if (!pressed && gamepadLtPressed && active?.source === 'gamepad-slot-0') {
      fireActive();
    }
    gamepadLtPressed = pressed;

    if (active?.source !== 'gamepad-slot-0') return;
    if (deps.getOwnedPlayerIds().length !== 1) {
      cancelActive();
      return;
    }
    const aim = gamepad.getAim(GAMEPAD_INDEX);
    setSelected(emoteIdFromVector(aim.x, aim.y, GAMEPAD_AIM_DEADZONE));
  }

  keyboardTarget.addEventListener('keydown', handleKeydown);
  keyboardTarget.addEventListener('keyup', handleKeyup);
  pointerTarget.addEventListener('pointermove', handlePointerMove, true);
  pointerTarget.addEventListener('pointerdown', handlePointerDown, true);
  pointerTarget.addEventListener('blur', handleBlur);
  documentRef.addEventListener('visibilitychange', handleVisibilityChange);

  function tick(): void {
    if (disposed) return;
    refreshEmoteButtonVisibility();
    if (!isEmoteInputEnabled()) {
      cancelActive();
      gamepadLtPressed = false;
      return;
    }
    tickGamepad();
  }

  wheel.onFire((emoteId) => {
    fireEmoteId(emoteId);
  });

  emoteButton.addEventListener('click', (event) => {
    if (disposed) return;
    event.preventDefault();
    event.stopPropagation();
    openForPointerButton();
  });

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    keyboardTarget.removeEventListener('keydown', handleKeydown);
    keyboardTarget.removeEventListener('keyup', handleKeyup);
    pointerTarget.removeEventListener('pointermove', handlePointerMove, true);
    pointerTarget.removeEventListener('pointerdown', handlePointerDown, true);
    pointerTarget.removeEventListener('blur', handleBlur);
    documentRef.removeEventListener('visibilitychange', handleVisibilityChange);
    emoteButton.remove();
    cancelActive();
    bubbles.dispose();
    unsubscribeSfx();
    wheel.dispose();
  }

  return {
    tick,
    isOpen: () => active != null,
    clearPlayer: bubbles.clearPlayer,
    dispose,
  };
}
